import React from "react";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../api/auth";
import { MeResponse, getMe } from "../api/meApi";

/**
 * Shared identity & role state for the signed-in user.
 *
 * One /me call is fired when MeProvider mounts (typically right after MSAL
 * authentication completes) and the result is stashed in context. Every page
 * and the AppLayout consume the same value via useMe(), so role-aware UI
 * (hiding admin nav links for non-admins, showing per-owned-tool cards on
 * the home page) doesn't cost extra round trips.
 *
 * As a side effect, MeProvider also calls setAuthToken() with the freshly
 * acquired bearer token — so by the time any page mounts its own useEffect,
 * the shared auth module already holds a valid token. Pages still re-acquire
 * their own token (MSAL silent acquisition is cached so this is a no-op
 * round-trip-wise), but this means tools-like pages don't briefly send
 * unauthenticated requests if MeProvider got there first.
 */

interface MeContextValue {
  me: MeResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const MeContext = React.createContext<MeContextValue>({
  me: null,
  loading: true,
  error: null,
  refetch: async () => {},
});

export const MeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [me,      setMe]      = React.useState<MeResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!account) return;
    setLoading(true);
    setError(null);
    try {
      await getToken(instance, account);
      const fetched = await getMe();
      setMe(fetched);
    } catch (err) {
      setError("We couldn't verify your access. Please sign out and sign back in — if this keeps happening, contact IT.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [account, instance]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <MeContext.Provider value={{ me, loading, error, refetch: load }}>
      {children}
    </MeContext.Provider>
  );
};

export function useMe(): MeContextValue {
  return React.useContext(MeContext);
}
